import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Position } from "@blueprintjs/core";
import { Popover } from "@blueprintjs/core";
import classNames from "classnames";
import { omit } from "lodash";
import ScrollBar from "components/ScrollBar";
import IconButton from "components/IconButton";
import HtmlText from "components/HtmlText";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { scrollElementSelector } from "store/global/global.selectors";
import { showOverlaySelector } from "store/ps-workspace/ps-workspace.selectors";
import useOutsideClick from "hooks/useClickOutside";
import { useOnScrollPopoverStateListener } from "hooks/useScrollDebounceListener";
import { defaultPopoverProps } from "shared/defaultPopoverProps";
import QuestionMarkIcon from "icons/questionmark.svg";
import * as styles from "./styles.module.scss";
const InfoIconWithTooltip = ({ classes, tooltipText, scrollElement, position, learnMoreUrl, learnMoreLinkInline = true, }) => {
    const rootRef = useRef(null);
    const location = useLocation();
    const [popoverRef, setPopoverRef] = useState(null);
    const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
    const elementScroll = useSelector(scrollElementSelector(scrollElement));
    const showOverlay = useSelector(showOverlaySelector(false, false));
    /** TEMP, to overrule hardcoded condition to hide popover on PS workspaces with overlay visible  */
    const isRecipeScaling = useMemo(() => location.pathname.startsWith("/recipe-scaling"), [location]);
    const tooltipContent = useMemo(() => {
        if (!tooltipText && !learnMoreUrl) {
            return "";
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(HtmlText, { text: tooltipText }),
            learnMoreUrl ? (React.createElement(React.Fragment, null,
                " ",
                React.createElement(LearnMoreLink, { inline: learnMoreLinkInline, url: learnMoreUrl }))) : undefined));
    }, [learnMoreLinkInline, learnMoreUrl, tooltipText]);
    const { isOutside } = useOutsideClick(rootRef, [popoverRef]);
    useOnScrollPopoverStateListener(tooltipIsOpen, setTooltipIsOpen, popoverRef, elementScroll);
    useEffect(() => {
        isOutside && setTooltipIsOpen(false);
    }, [isOutside]);
    return (React.createElement("div", { ref: rootRef, className: classNames(styles.root, classes?.className) },
        React.createElement(Popover, { ...defaultPopoverProps, popoverRef: setPopoverRef, content: React.createElement(ScrollBar, { classes: { view: styles.scrollBar }, autoHeight: true, autoHeightMax: 300 },
                React.createElement("p", { className: styles.content }, tooltipContent)), isOpen: (!showOverlay || isRecipeScaling) && tooltipIsOpen, position: position ?? Position.TOP, portalClassName: classNames(styles.popoverPortal, classes?.portalClassName), onInteraction: (nextOpenState) => tooltipIsOpen && setTooltipIsOpen(nextOpenState), modifiers: {
                offset: { enabled: true, options: { offset: [0, 8] } },
                flip: { options: { padding: { top: 36 } } },
            }, renderTarget: (props) => (React.createElement("div", { ...omit(props, "isOpen") },
                React.createElement(IconButton, { title: "Info", small: true, icon: React.createElement(QuestionMarkIcon, null), classes: {
                        root: classNames(styles.icon, classes?.iconClassName, {
                            [styles.tooltipOpened]: tooltipIsOpen,
                        }),
                    }, onClick: () => tooltipText && setTooltipIsOpen(!tooltipIsOpen) }))) })));
};
export default InfoIconWithTooltip;
